import { getAudioMap, getCurriculum } from '../content/curriculum';
import { WORD_TYPE } from './constants';
import getAudio from './audioUtils';

export const getOnboardingWords = (type) => {
  const { onboardings = {} } = getCurriculum();
  const { words = {} } = onboardings[type] || onboardings[WORD_TYPE.WORD_VC] || {};
  return words;
};

/**
 * Returns the loaded onboarding assets for the given word type.
 * @param {WORD_TYPE} type - Word type of the current level
 */
export const getLoadedOnboardingAssets = (type) => {
  const { onboarding, instruction } = getAudioMap();
  const words = getOnboardingWords(type);
  switch (type) {
    case WORD_TYPE.WORD_VC:
      return ({
        words: {
          target: 'mat',
          targetletters: ['mat'], // Needed by word button rendering
          distractors: ['mom'],
        },
        // Before highlighting a word
        step1: [
          getAudio(onboarding.common.helpthecurioso),
          getAudio(onboarding.vc.step1),
          getAudio(onboarding.common.soundout),
        ],
        // Highlight target
        step2: [
          getAudio(onboarding.vc.targetlong),
          getAudio(onboarding.vc.target),
        ],
        // Highlight distractor
        step3: [
          getAudio(onboarding.vc.distractorlong),
          getAudio(onboarding.vc.distractor),
        ],
        // Click correct instruction
        final1: [
          getAudio(onboarding.vc.step2),
          getAudio(onboarding.common.clickhere),
        ],
        // Post land on log, give control to game
        final2: [
          getAudio(onboarding.vc.target),
          getAudio(onboarding.common.youregoingtohearmore),
          getAudio(instruction.clicktheword),
        ],
      });
    case WORD_TYPE.WORD_COMBINATIONS:
      return ({
        words: {
          target: 'chat',
          targetletters: ['chat'], // Needed by word button rendering
          distractors: ['than'],
        },
        // Before highlighting a word
        step1: [
          getAudio(onboarding.common.helpthecurioso),
          getAudio(onboarding.combinations.step1),
          getAudio(onboarding.common.soundout),
        ],
        // Highlight target
        step2: [
          getAudio(onboarding.combinations.targetlong),
          getAudio(onboarding.combinations.target),
        ],
        // Highlight distractor
        step3: [
          getAudio(onboarding.combinations.distractorlong),
          getAudio(onboarding.combinations.distractor),
        ],
        // Click correct instruction
        final1: [
          getAudio(onboarding.combinations.step2),
          getAudio(onboarding.common.clickhere),
        ],
        // Post land on log, give control to game
        final2: [
          getAudio(onboarding.combinations.target),
          getAudio(onboarding.common.youregoingtohearmore),
          getAudio(instruction.clicktheword),
        ],
      });

    case WORD_TYPE.AUTOMATICITY:
      return ({
        words,
        // Before highlighting a word
        step1: [
          getAudio(onboarding.automaticity.step1),
          getAudio(onboarding.automaticity.step2),
          getAudio(onboarding.automaticity.step3),
        ],
        // Highlight target
        step2: [getAudio(onboarding.automaticity.step4)],
        // Highlight distractor
        step3: [getAudio(onboarding.automaticity.step5)],
        // Click correct instruction
        final1: [getAudio(onboarding.automaticity.step6)],
        // Post land on log, give control to game
        final2: [
          getAudio(onboarding.automaticity.step7),
          getAudio(onboarding.common.youregoingtohearmore),
          getAudio(instruction.clicktheword),
          getAudio(onboarding.automaticity.step8),
        ],
        fail: [getAudio(onboarding.automaticity.fail)],
      });

    case WORD_TYPE.WORD_INFLECTED:
      return ({
        words,
        step1: [
          getAudio(onboarding.common.helpthecurioso),
          getAudio(onboarding.inflected.step1),
        ],
        step2: [getAudio(onboarding.inflected.step2)],
        step3: [getAudio(onboarding.inflected.step3)],
        step4: [getAudio(onboarding.inflected.step4)],
        step5: [getAudio(onboarding.inflected.step5)],
        step6: [getAudio(onboarding.inflected.step6)],
        step7: [
          getAudio(onboarding.inflected.endings_are),
          getAudio(onboarding.inflected.sometimes),
          getAudio(onboarding.inflected.when),
        ],
        // Click here
        final1: [getAudio(onboarding.common.clickhere)],
        // Post land on log, give control to game
        final2: [
          getAudio(onboarding.inflected.target),
          getAudio(onboarding.common.youregoingtohearmore),
          getAudio(instruction.clicktheword),
        ],
        fail: [], // Only automaticity uses this
      });

    case WORD_TYPE.WORD_PREFIX:
      return ({
        words,
        step1: [
          getAudio(onboarding.common.helpthecurioso),
          getAudio(onboarding.prefix.step1),
        ],
        step2: [getAudio(onboarding.prefix.step2)],
        step3: [getAudio(onboarding.prefix.step3)],
        step4: [getAudio(onboarding.prefix.step4)],
        step5: [getAudio(onboarding.prefix.step5)],
        step6: [getAudio(onboarding.prefix.step6)],
        step7: [
          getAudio(onboarding.prefix.wecan),
          getAudio(onboarding.prefix.thereare),
          getAudio(onboarding.prefix.sometimes),
          getAudio(onboarding.prefix.when),
        ],
        // Click here
        final1: [getAudio(onboarding.common.clickhere)],
        // Post land on log, give control to game
        final2: [
          getAudio(onboarding.prefix.target),
          getAudio(onboarding.common.youregoingtohearmore),
          getAudio(instruction.clicktheword),
        ],
        fail: [], // Only automaticity uses this
      });

    case WORD_TYPE.WORD_TWO_SYLLABLE:
      return ({
        words,
        step1: [
          getAudio(onboarding.common.helpthecurioso),
          getAudio(onboarding.twosyllable.step1),
        ],
        step2: [getAudio(onboarding.twosyllable.step2)],
        step3: [getAudio(onboarding.twosyllable.step3)],
        step4: [getAudio(onboarding.twosyllable.step4)],
        step5: [getAudio(onboarding.twosyllable.step5)],
        step6: [getAudio(onboarding.twosyllable.step6)],
        step7: [
          getAudio(onboarding.twosyllable.step7),
          getAudio(onboarding.twosyllable.step8),
        ],
        step8: [getAudio(onboarding.twosyllable.step9)],
        step9: [getAudio(onboarding.twosyllable.step10)],
        step10: [getAudio(onboarding.twosyllable.step11)],
        step11: [
          getAudio(onboarding.twosyllable.step12),
          getAudio(onboarding.twosyllable.step13),
          getAudio(onboarding.twosyllable.step14),
        ],
        final1: [getAudio(onboarding.common.clickhere)],
        final2: [
          getAudio(onboarding.twosyllable.target),
          getAudio(onboarding.common.youregoingtohearmore),
          getAudio(instruction.clicktheword),
        ],
      });

    case WORD_TYPE.WORD_MULTI_SYLLABLE:
      return ({
        words,
        step1: [
          getAudio(onboarding.common.helpthecurioso),
          getAudio(onboarding.multisyllable.step1),
        ],
        step2: [getAudio(onboarding.multisyllable.step2)],
        step3: [getAudio(onboarding.multisyllable.step3)],
        step4: [getAudio(onboarding.multisyllable.step4)],
        step5: [getAudio(onboarding.multisyllable.step5)],
        step6: [getAudio(onboarding.multisyllable.step6)],
        step7: [getAudio(onboarding.multisyllable.step7)],
        step8: [
          getAudio(onboarding.multisyllable.step8),
          getAudio(onboarding.multisyllable.step9),
        ],
        step9: [getAudio(onboarding.multisyllable.step10)],
        step10: [getAudio(onboarding.multisyllable.step11)],
        step11: [getAudio(onboarding.multisyllable.step12)],
        step12: [getAudio(onboarding.multisyllable.step13)],
        step13: [
          getAudio(onboarding.multisyllable.step14),
          getAudio(onboarding.multisyllable.step15),
        ],
        final1: [getAudio(onboarding.common.clickhere)],
        final2: [
          getAudio(onboarding.multisyllable.step7), // Target
          getAudio(onboarding.common.youregoingtohearmore),
          getAudio(instruction.clicktheword),
        ],
      });

    default:
      return ({
        words: {
          target: 'mat',
          targetletters: ['mat'],
          distractors: ['mom'],
        },
        step1: [
          getAudio(onboarding.common.helpthecurioso),
          getAudio(onboarding.vc.step1),
          getAudio(onboarding.common.soundout),
        ],
        step2: [
          getAudio(onboarding.vc.targetlong),
          getAudio(onboarding.vc.target),
        ],
        step3: [
          getAudio(onboarding.vc.distractorlong),
          getAudio(onboarding.vc.distractor),
        ],
        final1: [
          getAudio(onboarding.vc.step2),
          getAudio(onboarding.common.clickhere),
        ],
        final2: [
          getAudio(onboarding.vc.target),
          getAudio(onboarding.common.youregoingtohearmore),
          getAudio(instruction.clicktheword),
        ],
      });
  }
};

export const getOnboardingStaticWords = (wordType) => {
  const data = getOnboardingWords(wordType);
  const retArr = [];
  retArr.push(data.target);
  data.distractors.forEach(entry => retArr.push(entry));
  return retArr; // returns static word array
};
